import styled from 'styled-components';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTranslation } from '@hooks';
import { useCart } from '@context/CartContext';

import NewCloseButton from '@components/ui/NewCloseButton';
import CartServices from '@services/CartServices';
import { addToBasketEvent } from '@lib/gtm';
import { truncateString } from '@utils';
import { NewButton } from '@components/ui';
import { StyledRow } from '@components/styles/CommonStyles';
import { Caption, Caption2 } from '@components/styles/Typography';
import AddToCartButton from './AddtoCartButton';
import ProductAddedAlert from './ProductAddedAlert';
import SizePicker from './NewSizePicker';

const MobileBuyMenu = ({
    product,
    selectedSize,
    setSelectedSize,
    onProductZoom,
    discountRate,
    showWarning,
    setShowWarning,
    showError,
    setShowError,
    stockModalValue,
    setStockModalValue,
    setVariantQuantity,
    variantQuantity,
}) => {
    const { locale, useCurrency, t } = useTranslation();
    const [isSizePickerOpen, setIsSizePickerOpen] = useState(false);
    const [isProductAddedAlertOpen, setIsProductAddedAlertOpen] = useState(
        false,
    );

    const { basketHash, currentUser } = useCart();
    const queryClient = useQueryClient();
    const addToCartMutation = useMutation({
        mutationFn: CartServices.addToCart,
        onSuccess: (data, { products: addedProducts }) => {
            addToBasketEvent({ products: data.data.products, addedProducts });
            queryClient.invalidateQueries(['getCart']);
        },
    });

    const { name, sizes, stock, comming, sku } = product;
    const openSizePicker = () => setIsSizePickerOpen(true);

    const closeSizePicker = () => {
        setIsSizePickerOpen(false);
        setSelectedSize('');
        setShowWarning(false);
    };

    const openProductAddedAlert = () => {
        closeSizePicker();
        setIsProductAddedAlertOpen(true);
    };

    const closeProductAddedAlert = () => setIsProductAddedAlertOpen(false);

    const priceBySize = (priceType) => {
        if (selectedSize) {
            return sizes.find((s) => s.id === selectedSize)[priceType];
        }
        return product[priceType];
    };

    const handleClick = async () => {
        if (variantQuantity.length > 0) {
            addToCartMutation.mutate({
                basketHash,
                products: variantQuantity,
                token: currentUser,
            });
            closeSizePicker();
            setSelectedSize('');
            openProductAddedAlert();
            setShowWarning(false);
            setVariantQuantity([]);
        } else {
            openSizePicker();
            if (isSizePickerOpen) {
                setShowWarning(true);
            }
        }
    };

    const discountedPrice = useCurrency(priceBySize('discounted_price'));

    const price = useCurrency(priceBySize('price'));

    return (
        <MobileMenuWrapper onProductZoom={onProductZoom}>
            <SizePickerWrapper sizePickerOpen={isSizePickerOpen}>
                <NewCloseButton
                    close={closeSizePicker}
                    style={{
                        position: 'absolute',
                        right: '15px',
                        top: '25px',
                        zIndex: '2',
                    }}
                />

                <SizePicker
                    testId="size-picker-pop-up"
                    optionTestId="size-option-pop-up"
                    selectedSize={selectedSize}
                    setSelectedSize={setSelectedSize}
                    showWarning={showWarning}
                    sizes={sizes}
                    showError={showError}
                    setShowError={setShowError}
                    stock={stock}
                    product={product}
                    setVariantQuantity={setVariantQuantity}
                    variantQuantity={variantQuantity}
                    setShowWarning={setShowWarning}
                    stockModalValue={stockModalValue}
                    setStockModalValue={setStockModalValue}
                    isSizePickerOpen={isSizePickerOpen}
                    mobileBuyMenu
                />
            </SizePickerWrapper>
            <ProductAddedAlert
                discountRate={discountRate}
                isOpen={isProductAddedAlertOpen}
                onClose={closeProductAddedAlert}
            />
            <StyledRow margin="0 0 10px 0" gap="5px">
                <TitleWrapper>
                    <Caption2>{truncateString(name[locale], 40)}</Caption2>
                    <Caption2 style={{ margin: '5px 0' }}>
                        Unit Code: {sku}
                    </Caption2>
                </TitleWrapper>

                <PriceContainer>
                    {discountRate > 0 && (
                        <PriceWrapper>
                            <Caption2 color="bgGray25">{price}</Caption2>
                        </PriceWrapper>
                    )}
                    {discountRate > 0 ? (
                        <StyledRow justifyContent="center" margin="4px 0 0">
                            <DiscountRateWrapper>
                                <Caption color="secondary">
                                    -%{discountRate}
                                </Caption>
                            </DiscountRateWrapper>
                            <Caption size="10px" letterSpacing="1px">
                                {discountedPrice}
                            </Caption>
                        </StyledRow>
                    ) : (
                        <Caption>{price}</Caption>
                    )}
                </PriceContainer>
            </StyledRow>
            {isSizePickerOpen ? (
                <AddToCartButton
                    comming={comming}
                    onButtonClick={handleClick}
                />
            ) : (
                <NewButton
                    size="xlarge"
                    type="primary"
                    data-testid="add-to-cart-button"
                    disabled={comming}
                    handleClick={handleClick}
                    content={comming ? 'COMING SOON' : t('addToBasket')}
                />
            )}
        </MobileMenuWrapper>
    );
};
const MobileMenuWrapper = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    padding: 15px;
    background-color: white;
    border-top: 1px solid black;
    z-index: 998;
    transition: all 0.5s ease-in-out;
    transform: ${({ onProductZoom }) => onProductZoom && 'translateY(100%)'};

    @media (min-width: 1025px) {
        display: none;
    }
`;
const PriceContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    width: max-content;
    height: 100%;
`;
const PriceWrapper = styled.div`
    span {
        text-decoration: line-through;
    }
`;
const DiscountRateWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.primary};
    padding: 0 3px;
    margin-right: 8px;
`;
const SizePickerWrapper = styled.div`
    width: 100%;
    position: absolute;
    background-color: ${({ theme }) => theme.colors.secondary};
    top: -1px;
    left: 0;
    transform: ${({ sizePickerOpen }) =>
        sizePickerOpen ? 'translateY(-100%)' : 'translateY(100%)'};
    opacity: ${({ sizePickerOpen }) => (sizePickerOpen ? '1' : '0')};
    height: ${({ sizePickerOpen }) => (sizePickerOpen ? 'auto' : '0')};
    overflow: ${({ sizePickerOpen }) => (sizePickerOpen ? 'auto' : 'hidden')};
    padding: ${({ sizePickerOpen }) =>
        sizePickerOpen ? '25px 15px 22px 15px' : '0'};
    transition: all 0.5s ease-in-out;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.3);
        z-index: 2;
        transform: translateY(-100%);
    }

    @media (min-width: 1024px) {
        display: none;
    }
`;
const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;
export default MobileBuyMenu;
